"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProjectGallery() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const projects = [{
    number: "—01",
    title: "Standing Seam Metal",
    location: "Calgary",
    year: "2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/standing_seam_calgary.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/standing_seam_calgary.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/standing_seam_calgary.webp-wvc-sizes",
    alt: "Premium standing seam metal roof installation in Calgary"
  }, {
    number: "—02",
    title: "James Hardie Siding",
    location: "Airdrie",
    year: "2023",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/hardie_siding_airdrie.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/hardie_siding_airdrie.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/hardie_siding_airdrie.png-wvc-sizes",
    alt: "Modern James Hardie siding renovation in Airdrie"
  }, {
    number: "—03",
    title: "Architectural Cladding",
    location: "Cochrane",
    year: "2023",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/architectural_cladding_cochrane.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/architectural_cladding_cochrane.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/architectural_cladding_cochrane.png-wvc-sizes",
    alt: "Custom architectural cladding project in Cochrane"
  }, {
    number: "—04",
    title: "Stone Veneer Accents",
    location: "Okotoks",
    year: "2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/stone_veneer_okotoks.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/stone_veneer_okotoks.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/stone_veneer_okotoks.png-wvc-sizes",
    alt: "Luxury stone veneer exterior finish in Okotoks"
  }, {
    number: "—05",
    title: "ACM Panel System",
    location: "Chestermere",
    year: "2022",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/acm_panels_chestermere.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/acm_panels_chestermere.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/acm_panels_chestermere.png-wvc-sizes",
    alt: "High-end ACM panel installation in Chestermere"
  }, {
    number: "—06",
    title: "Luxury Metal Roofing",
    location: "Calgary",
    year: "2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/luxury_metal_roof_calgary.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/luxury_metal_roof_calgary.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/luxury_metal_roof_calgary.png-wvc-sizes",
    alt: "Premium luxury metal roofing project in Calgary"
  }];
  return;
}
type Project = {
  number: string;
  title: string;
  location: string;
  year: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
function ProjectCard({
  project,
  className = "",
  style,
  aspectClass,
  ...rest
}: {
  project: Project;
  className?: string;
  style?: React.CSSProperties;
  aspectClass: string;
  "data-index"?: number;
}) {
  return <div className={className} style={style} {...rest}>
      <Link to="/ContactPage" className="group block relative cursor-pointer">
        {}
        <div className={`relative overflow-hidden ${aspectClass} bg-muted rounded-lg`}>
          <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] ease-out group-hover:scale-[1.03]" onError={e => {
          const target = e.target as HTMLImageElement;
          target.src = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/placeholder.png";
          target.removeAttribute('data-wvc-srcset');
          target.removeAttribute('data-wvc-sizes');
        }} />

          {}
          <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary/60 opacity-0 group-hover:opacity-40 transition-opacity duration-1000 ease-out" aria-hidden="true" />

          {}
          <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10">
            <span className="font-default text-xs md:text-sm tracking-[0.2em] text-white">
              {project.number}
            </span>
          </div>

          {}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out z-10">
            <h3 className="font-default font-bold text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-3">
              {project.title}
            </h3>
            <p className="font-default text-[10px] md:text-xs tracking-[0.25em] uppercase text-white/90">
              {project.location}, {project.year}
            </p>
          </div>
        </div>

        {}
        <div className="md:hidden mt-4 flex items-baseline justify-between gap-4">
          <h3 className="font-default font-bold text-2xl text-foreground leading-tight">
            {project.title}
          </h3>
          <p className="font-default text-[10px] tracking-[0.2em] uppercase text-muted-foreground whitespace-nowrap">
            {project.location}, {project.year}
          </p>
        </div>
      </Link>
    </div>;
}